import { useState, useEffect } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

let _cached = null;

/**
 * Best guess at the visitor's currency from the browser alone.
 *
 * Aug 2026 — this exists because the previous version hard-defaulted to USD
 * and only ever corrected itself if GET /api/sub/region answered. When that
 * endpoint was down (it 502'd for a period in August) every Indian visitor saw
 * dollar prices on the homepage and on /pricing, with nothing in the UI
 * suggesting anything had failed.
 *
 * The signal is the same one the standalone v4 HTML used: the IANA timezone,
 * plus the locale as a secondary check. It is not authoritative — a VPN or a
 * traveller's laptop will read wrong — which is exactly why it is only a
 * FALLBACK. The server's Cloudflare-backed answer always wins when it arrives.
 */
function guessRegionFromBrowser() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const lang = navigator.language || "";
    const india = /Kolkata|Calcutta/i.test(tz) || /-IN$/i.test(lang);
    if (india) {
      return { country: "IN", currency: "INR", symbol: "₹", is_india: true, source: "browser" };
    }
  } catch {
    /* Intl or navigator unavailable — fall through to the USD default. */
  }
  return { country: null, currency: "USD", symbol: "$", is_india: false, source: "default" };
}

export function useRegion() {
  // Seed from the browser guess rather than an unconditional USD, so the very
  // first paint is already right for the common case.
  const [region, setRegion] = useState(_cached || guessRegionFromBrowser);
  const [loading, setLoading] = useState(!_cached);

  useEffect(() => {
    if (_cached) return;
    let alive = true;
    fetch(`${BACKEND}/api/sub/region`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        if (!alive) return;
        // `source: "default"` means the server could not detect a country
        // either. Overwriting a positive browser signal with that would
        // actively make the answer worse, so keep what we have.
        if (!data || data.source === "default") {
          setLoading(false);
          return;
        }
        _cached = data;
        setRegion(data);
        setLoading(false);
      })
      .catch(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const price = (plan) => {
    if (!plan) return "";
    const val = region.is_india ? plan.price_inr : plan.price_usd;
    if (val === 0) return "Free";
    return `${region.symbol}${val}`;
  };

  return { region, loading, price };
}
